import { CheckCircle, Clock, Mail, Phone, ArrowLeft, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";

export default function LeadThankYouPage() {
  return (
    <div className="min-h-screen bg-galvani-bg relative overflow-hidden" data-testid="lead-thank-you-page">
      {/* Decorative orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-blue-300/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-300/30 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-pink-200/20 rounded-full blur-3xl" />

      <main className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-8 shadow-2xl shadow-green-500/30"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>

          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-galvani-text mb-6 leading-tight">
            ¡Solicitud <span className="gradient-text">recibida</span>!
          </h1>

          <p className="font-body text-lg text-galvani-textLight max-w-2xl mx-auto leading-relaxed">
            Hemos recibido tu solicitud correctamente. Uno de nuestros especialistas revisará tu proyecto
            y se pondrá en contacto contigo en <span className="text-galvani-text font-semibold">menos de 24 horas laborables</span>.
          </p>
        </motion.div>

        {/* What happens next */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-white border border-slate-200 rounded-3xl shadow-xl p-8 md:p-10 mb-10"
        >
          <h2 className="font-heading font-bold text-2xl text-galvani-text mb-6 text-center">
            ¿Qué pasa ahora?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                <span className="font-heading font-extrabold text-white text-xl">1</span>
              </div>
              <h3 className="font-heading font-semibold text-galvani-text mb-2">Revisamos tu proyecto</h3>
              <p className="text-sm text-galvani-textLight leading-relaxed">
                Analizamos tus objetivos, sector y presupuesto para preparar la mejor propuesta.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                <span className="font-heading font-extrabold text-white text-xl">2</span>
              </div>
              <h3 className="font-heading font-semibold text-galvani-text mb-2">Te llamamos en 24h</h3>
              <p className="text-sm text-galvani-textLight leading-relaxed">
                Una llamada de 20 min para entender bien tu proyecto y resolver dudas.
              </p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center mb-4">
                <span className="font-heading font-extrabold text-white text-xl">3</span>
              </div>
              <h3 className="font-heading font-semibold text-galvani-text mb-2">Propuesta a medida</h3>
              <p className="text-sm text-galvani-textLight leading-relaxed">
                En 48h tendrás una propuesta con formatos, timings y presupuesto claro.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="bg-gradient-to-br from-galvani-blue via-galvani-purple to-galvani-pink rounded-3xl p-8 md:p-10 mb-10 shadow-2xl"
        >
          <div className="text-center text-white">
            <Clock className="w-10 h-10 mx-auto mb-4 opacity-80" />
            <h3 className="font-heading font-bold text-2xl mb-3">
              ¿Tienes prisa o quieres avanzar?
            </h3>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Contáctanos directamente y agilizamos el proceso.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://wa.me/34683397873" target="_blank" rel="noopener noreferrer">
                <Button className="bg-white text-galvani-purple hover:bg-slate-100 font-body font-semibold px-6 h-12 rounded-full" data-testid="lead-thankyou-whatsapp">
                  <MessageCircle size={18} className="mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a href="tel:+34683397873">
                <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 hover:text-white font-body font-semibold px-6 h-12 rounded-full" data-testid="lead-thankyou-phone">
                  <Phone size={18} className="mr-2" />
                  +34 683 39 78 73
                </Button>
              </a>
              <a href="mailto:info@galvanistudios.com">
                <Button variant="outline" className="bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 hover:text-white font-body font-semibold px-6 h-12 rounded-full" data-testid="lead-thankyou-email">
                  <Mail size={18} className="mr-2" />
                  Email
                </Button>
              </a>
            </div>
          </div>
        </motion.div>

        {/* Back home */}
        <div className="text-center">
          <a href="/" className="inline-flex items-center gap-2 text-galvani-textLight hover:text-galvani-purple font-body transition-colors" data-testid="lead-thankyou-back-home">
            <ArrowLeft size={18} />
            Volver al inicio
          </a>
        </div>
      </main>
    </div>
  );
}
